import { useEffect, useMemo, useRef, useState } from "react";
import {
  DrawData,
  GcProgressItem,
  InspectorForm,
  Metrics,
  Photo,
  Project,
  ResidentialPhase,
  SovItem,
  TabId,
} from "../types/inspector.types";
import { statusOptions, tabItems } from "../constants/inspectorData";
import useScrollToTop from "../../../../hooks/useScrollToTop";
import TopBar from "../components/TopBar";
import TabBar from "../components/TabBar";
import ResidentialFiTab from "../components/ResidentialFiTab";
import DrawTab from "../components/DrawTab";
import PhotosTab from "../components/PhotosTab";
import ResidentialNotesTab from "../components/ResidentialNotesTab";
import ResidentialSubmitTab from "../components/ResidentialSubmitTab";
import ProjectSwitcherModal from "../components/ProjectSwitcherModal";

interface ResidentialInspectorPageProps {
  currentProject: Project;
  projects: Project[];
  currentProjectId: string;
  phases: ResidentialPhase[];
  gcBilledPct: string;
  sovItems: SovItem[];
  gcProgress: GcProgressItem[];
  photos: Photo[];
  generalNotes: string;
  flagNotes: string;
  drawNarrative: string;
  scheduleNotes: string;
  bankNotes: string;
  inspectorForm: InspectorForm;
  drawData: DrawData;
  drawPercentage: string;
  metrics: Metrics;
  isSaving: boolean;
  isSubmitting: boolean;
  saveError: string;
  submitError: string;
  saveDraftSuccess: boolean;
  submitted: boolean;
  onSetStatus: (id: string, status: number) => void;
  onSetCustomPct: (id: string, value: string) => void;
  onClearCustomPct: (id: string) => void;
  onToggleFlag: (id: string) => void;
  onFlagWithComment: (id: string, text: string) => void;
  onAddComment: (id: string, text: string) => void;
  onAddPhoto: (photos: Photo[]) => void;
  onUpdateCaption: (id: string, caption: string) => void;
  onPhotoUploaded: (id: string, s3Key: string) => void;
  onPhotoUploadFailed: (id: string, error: string) => void;
  onRemovePhoto: (id: string) => void;
  onGeneralNotesChange: (val: string) => void;
  onFlagNotesChange: (val: string) => void;
  onDrawNarrativeChange: (val: string) => void;
  onScheduleNotesChange: (val: string) => void;
  onBankNotesChange: (val: string) => void;
  onInspectorFormChange: (field: keyof InspectorForm, value: string) => void;
  onSubmit: () => void;
  onSaveDraft: () => void;
  onNewInspection: () => void;
  onProjectSelect: (id: string) => void;
  onOpenDirections: () => void;
}

export default function ResidentialInspectorPage({
  currentProject,
  projects,
  currentProjectId,
  phases,
  gcBilledPct,
  sovItems,
  gcProgress,
  photos,
  generalNotes,
  flagNotes,
  drawNarrative,
  scheduleNotes,
  bankNotes,
  inspectorForm,
  drawData,
  drawPercentage,
  metrics,
  isSaving,
  isSubmitting,
  saveError,
  submitError,
  saveDraftSuccess,
  submitted,
  onSetStatus,
  onSetCustomPct,
  onClearCustomPct,
  onToggleFlag,
  onFlagWithComment,
  onAddComment,
  onAddPhoto,
  onUpdateCaption,
  onPhotoUploaded,
  onPhotoUploadFailed,
  onRemovePhoto,
  onGeneralNotesChange,
  onFlagNotesChange,
  onDrawNarrativeChange,
  onScheduleNotesChange,
  onBankNotesChange,
  onInspectorFormChange,
  onSubmit,
  onSaveDraft,
  onNewInspection,
  onProjectSelect,
  onOpenDirections,
}: ResidentialInspectorPageProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<TabId>("fi");
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [phaseCollapsed, setPhaseCollapsed] = useState<Record<string, boolean>>(
    { "2": true, "3": true, "4": true },
  );

  const activeTabIndex = tabItems.findIndex((t) => t.id === activeTab);
  useScrollToTop(activeTabIndex, {
    targetRef: scrollRef as React.RefObject<HTMLElement | null>,
  });
  useScrollToTop(submitted ? 1 : 0, {
    targetRef: scrollRef as React.RefObject<HTMLElement | null>,
  });

  useEffect(() => {
    if (submitted) setActiveTab("submit");
  }, [submitted]);

  const flaggedItems = useMemo(
    () =>
      phases
        .flatMap((p) => p.items)
        .filter((item) => item.flagged)
        .map(
          (item) =>
            `⚠ ${item.num} – ${item.desc}: GC billed ${item.gcBilled}%.`,
        ),
    [phases],
  );

  const handleTogglePhase = (phaseId: string) => {
    setPhaseCollapsed((prev) => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  const handleNewInspection = () => {
    setActiveTab("fi");
    onNewInspection();
  };

  return (
    <div className="inspectorPage">
      <TopBar
        project={currentProject}
        onDirections={onOpenDirections}
        onSwitch={() => setShowProjectModal(true)}
      />
      <div className="inspectorContent">
      <TabBar
        tabs={tabItems}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        locked={submitted}
      />

      <div className="scroll" ref={scrollRef}>
        <div className={`screen${activeTab === "fi" ? " active" : ""}`}>
          <ResidentialFiTab
            project={currentProject}
            phases={phases}
            gcBilledPct={gcBilledPct}
            metrics={metrics}
            collapsed={phaseCollapsed}
            statusOptions={statusOptions}
            onTogglePhase={handleTogglePhase}
            onSetStatus={onSetStatus}
            onSetCustomPct={onSetCustomPct}
            onClearCustomPct={onClearCustomPct}
            onToggleFlag={onToggleFlag}
            onFlagWithComment={onFlagWithComment}
            onAddComment={onAddComment}
          />
        </div>

        <div className={`screen${activeTab === "draw" ? " active" : ""}`}>
          <DrawTab
            drawData={drawData}
            sovItems={sovItems}
            gcProgress={gcProgress}
            drawPercentage={drawPercentage}
            cscEstimation={String(metrics.avgPct)}
            flagNotes={flagNotes}
          />
        </div>

        <div className={`screen${activeTab === "photos" ? " active" : ""}`}>
          <PhotosTab
            photos={photos}
            onAddPhoto={onAddPhoto}
            onUpdateCaption={onUpdateCaption}
            onPhotoUploaded={onPhotoUploaded}
            onPhotoUploadFailed={onPhotoUploadFailed}
            onRemovePhoto={onRemovePhoto}
          />
        </div>

        <div className={`screen${activeTab === "notes" ? " active" : ""}`}>
          <ResidentialNotesTab
            generalNotes={generalNotes}
            drawNarrative={drawNarrative}
            flaggedNotes={flagNotes}
            scheduleNotes={scheduleNotes}
            bankNotes={bankNotes}
            flaggedItems={flaggedItems}
            onGeneralNotesChange={onGeneralNotesChange}
            onDrawNarrativeChange={onDrawNarrativeChange}
            onFlaggedNotesChange={onFlagNotesChange}
            onScheduleNotesChange={onScheduleNotesChange}
            onBankNotesChange={onBankNotesChange}
          />
        </div>

        <div className={`screen${activeTab === "submit" ? " active" : ""}`}>
          <ResidentialSubmitTab
            phases={phases}
            metrics={metrics}
            gcBilledPct={gcBilledPct}
            inspectorForm={inspectorForm}
            generalNotes={generalNotes}
            drawNarrative={drawNarrative}
            scheduleNotes={scheduleNotes}
            bankNotes={bankNotes}
            submitted={submitted}
            projectName={currentProject.name}
            isSaving={isSaving}
            isSubmitting={isSubmitting}
            saveError={saveError}
            submitError={submitError}
            saveDraftSuccess={saveDraftSuccess}
            onInspectorFormChange={onInspectorFormChange}
            onSubmit={onSubmit}
            onSaveDraft={onSaveDraft}
            onNewInspection={handleNewInspection}
          />
        </div>
      </div>
      </div>

      <ProjectSwitcherModal
        projects={projects}
        currentProjectId={currentProjectId}
        open={showProjectModal}
        onClose={() => setShowProjectModal(false)}
        onSelect={(id) => {
          onProjectSelect(id);
          setShowProjectModal(false);
        }}
      />
    </div>
  );
}
